import React, {
    useCallback,
    useEffect,
    useMemo,
} from "react";

import {
    FiAlertCircle,
    FiCreditCard,
    FiEye,
    FiFileText,
    FiLoader,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchSaleInvoicePaymentHistory,
    saleInvoicePaymentsSelector,
    saleInvoicePaymentLoadingSelector,
    saleInvoicePaymentErrorSelector,
    saleInvoicePaymentSummarySelector,
} from "../../../../../../../store/billing/saleInvoicePaymentSlice";

import "./SaleInvoicePaymentsPanel.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(parsed)
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const firstPositiveNumber = (
    values = [],
    fallback = 0
) => {
    for (const value of values) {
        const parsed =
            Number(value);

        if (
            Number.isFinite(parsed) &&
            parsed > 0
        ) {
            return parsed;
        }
    }

    return fallback;
};

const firstDefinedNumber = (
    values = [],
    fallback = 0
) => {
    for (const value of values) {
        if (
            value === undefined ||
            value === null ||
            value === ""
        ) {
            continue;
        }

        const parsed =
            Number(value);

        if (
            Number.isFinite(parsed)
        ) {
            return parsed;
        }
    }

    return fallback;
};

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
              }
            : {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
              }
    ).format(date);
};

const formatLabel = (
    value
) => {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace(/_/g, " ")
        .trim()
        .toLowerCase()
        .replace(
            /\b\w/g,
            (character) =>
                character.toUpperCase()
        );
};

const getInvoiceId = (
    invoice
) =>
    invoice?._id ||
    invoice?.id ||
    "";

const getInvoiceNumber = (
    invoice
) =>
    invoice?.documentNumber ||
    invoice?.invoiceNumber ||
    invoice?.invoiceNo ||
    "-";

const getCustomerId = (
    invoice
) => {
    if (
        invoice?.customer &&
        typeof invoice.customer ===
            "object"
    ) {
        return (
            invoice.customer._id ||
            invoice.customer.id ||
            ""
        );
    }

    if (
        typeof invoice?.customer ===
        "string"
    ) {
        return invoice.customer;
    }

    return (
        invoice?.customerId ||
        invoice?.customerSnapshot
            ?.customerId ||
        invoice?.customerSnapshot?._id ||
        ""
    );
};

const getWebsiteUserId = (
    invoice
) => {
    if (
        invoice?.websiteUser &&
        typeof invoice.websiteUser ===
            "object"
    ) {
        return (
            invoice.websiteUser._id ||
            invoice.websiteUser.id ||
            ""
        );
    }

    if (
        typeof invoice
            ?.websiteUser ===
        "string"
    ) {
        return invoice.websiteUser;
    }

    return (
        invoice?.websiteUserId ||
        ""
    );
};

const getPaymentRows = (
    state
) => {
    if (
        Array.isArray(state)
    ) {
        return state;
    }

    const candidates = [
        state?.payments,
        state?.records,
        state?.history,
        state?.paymentHistory,
        state?.allocations,
        state?.data,
        state?.data?.payments,
        state?.data?.records,
        state?.data?.history,
        state?.data?.allocations,
    ];

    return (
        candidates.find(
            Array.isArray
        ) || []
    );
};

/*
|--------------------------------------------------------------------------
| NORMALIZE PAYMENT
|--------------------------------------------------------------------------
*/

const normalizePayment = (
    item = {},
    invoice = {}
) => {
    const nestedPayment =
        item?.payment &&
        typeof item.payment ===
            "object"
            ? item.payment
            : {};

    const nestedAllocation =
        item?.allocation &&
        typeof item.allocation ===
            "object"
            ? item.allocation
            : {};

    const payment =
        Object.keys(
            nestedPayment
        ).length
            ? nestedPayment
            : item;

    const allocation =
        Object.keys(
            nestedAllocation
        ).length
            ? nestedAllocation
            : item;

    const paymentId =
        payment?._id ||
        item?.paymentId ||
        item?.payment?._id ||
        item?.sourcePaymentId ||
        null;

    const allocationId =
        nestedAllocation?._id ||
        item?.allocationId ||
        item?.paymentAllocation?._id ||
        (
            typeof item
                ?.paymentAllocation ===
            "string"
                ? item.paymentAllocation
                : null
        );

    /*
     * IMPORTANT:
     * Do not accept amount: 0 when the record clearly has
     * allocatedAmount/activeAmount greater than zero.
     */

    const allocatedAmount =
        roundMoney(
            firstDefinedNumber(
                [
                    payment?.allocatedAmount,
                    item?.allocatedAmount,
                    allocation?.allocatedAmount,
                    item?.activeAmount,
                    allocation?.activeAmount,
                ],
                0
            )
        );

    const activeAmount =
        roundMoney(
            firstDefinedNumber(
                [
                    allocation?.activeAmount,
                    item?.activeAmount,
                    allocatedAmount,
                ],
                0
            )
        );

    const reversedAmount =
        roundMoney(
            firstDefinedNumber(
                [
                    payment?.reversedAmount,
                    item?.reversedAmount,
                    allocation?.reversedAmount,
                ],
                0
            )
        );

    const directPaymentAmount =
        firstPositiveNumber(
            [
                payment?.amount,
                payment?.paymentAmount,
                payment?.receivedAmount,
                payment?.totalAmount,

                item?.amount,
                item?.paymentAmount,
                item?.receivedAmount,
                item?.totalAmount,

                item?.payment?.amount,
                item?.payment
                    ?.paymentAmount,
            ],
            0
        );

    /*
     * Legacy/history API may return amount: 0 while allocation
     * contains the actual paid amount.
     */

    const paymentAmount =
        roundMoney(
            directPaymentAmount > 0
                ? directPaymentAmount
                : Math.max(
                      allocatedAmount,
                      activeAmount,
                      0
                  )
        );

    const explicitAvailable =
        firstDefinedNumber(
            [
                payment?.unallocatedAmount,
                item?.unallocatedAmount,
                item?.payment
                    ?.unallocatedAmount,
            ],
            Number.NaN
        );

    const unallocatedAmount =
        roundMoney(
            Number.isFinite(
                explicitAvailable
            )
                ? Math.max(
                      explicitAvailable,
                      0
                  )
                : Math.max(
                      paymentAmount -
                          allocatedAmount,
                      0
                  )
        );

    const invoiceCustomerId =
        getCustomerId(invoice);

    const paymentCustomer =
        payment?.customer ||
        item?.customer ||
        null;

    let paymentCustomerId =
        "";

    if (
        paymentCustomer &&
        typeof paymentCustomer ===
            "object"
    ) {
        paymentCustomerId =
            paymentCustomer._id ||
            paymentCustomer.id ||
            "";
    } else if (
        typeof paymentCustomer ===
        "string"
    ) {
        paymentCustomerId =
            paymentCustomer;
    }

    paymentCustomerId =
        paymentCustomerId ||
        payment?.customerId ||
        item?.customerId ||
        invoiceCustomerId ||
        "";

    const invoiceWebsiteUserId =
        getWebsiteUserId(invoice);

    const paymentWebsiteUser =
        payment?.websiteUser ||
        item?.websiteUser ||
        null;

    let websiteUserId =
        "";

    if (
        paymentWebsiteUser &&
        typeof paymentWebsiteUser ===
            "object"
    ) {
        websiteUserId =
            paymentWebsiteUser._id ||
            paymentWebsiteUser.id ||
            "";
    } else if (
        typeof paymentWebsiteUser ===
        "string"
    ) {
        websiteUserId =
            paymentWebsiteUser;
    }

    websiteUserId =
        websiteUserId ||
        payment?.websiteUserId ||
        item?.websiteUserId ||
        invoiceWebsiteUserId ||
        "";

    const customerSnapshot = {
        ...(
            invoice?.customerSnapshot ||
            {}
        ),

        ...(
            payment?.customerSnapshot ||
            item?.customerSnapshot ||
            {}
        ),

        customerName:
            payment?.customerSnapshot
                ?.customerName ||
            item?.customerSnapshot
                ?.customerName ||
            payment?.customerName ||
            item?.customerName ||
            invoice?.customerSnapshot
                ?.customerName ||
            invoice?.customerSnapshot
                ?.name ||
            invoice?.customer?.name ||
            invoice?.customerName ||
            "",

        mobile:
            payment?.customerSnapshot
                ?.mobile ||
            item?.customerSnapshot
                ?.mobile ||
            payment?.customerMobile ||
            item?.customerMobile ||
            invoice?.customerSnapshot
                ?.mobile ||
            invoice?.customer?.mobile ||
            invoice?.customerMobile ||
            "",

        email:
            payment?.customerSnapshot
                ?.email ||
            item?.customerSnapshot
                ?.email ||
            payment?.customerEmail ||
            item?.customerEmail ||
            invoice?.customerSnapshot
                ?.email ||
            invoice?.customer?.email ||
            invoice?.customerEmail ||
            "",
    };

    const status =
        String(
            payment?.paymentStatus ||
            payment?.status ||
            item?.paymentStatus ||
            item?.status ||
            (
                unallocatedAmount <= 0
                    ? "FULLY_ALLOCATED"
                    : allocatedAmount > 0
                    ? "PARTIALLY_ALLOCATED"
                    : "COMPLETED"
            )
        )
            .trim()
            .toUpperCase();

    const allocationStatus =
        String(
            allocation?.status ||
            item?.allocationStatus ||
            "ACTIVE"
        )
            .trim()
            .toUpperCase();

    const isReversed =
        Boolean(
            payment?.isReversed ||
            item?.isReversed ||
            payment?.reversal
                ?.isReversed
        ) ||
        status === "REVERSED" ||
        allocationStatus ===
            "REVERSED";

    const document = {
        ...(
            item?.document &&
            typeof item.document ===
                "object"
                ? item.document
                : {}
        ),

        _id:
            item?.document?._id ||
            (
                typeof item
                    ?.document ===
                "string"
                    ? item.document
                    : null
            ) ||
            payment?.document?._id ||
            getInvoiceId(invoice),

        documentNumber:
            item?.document
                ?.documentNumber ||
            payment?.document
                ?.documentNumber ||
            item?.documentNumber ||
            getInvoiceNumber(invoice),

        documentDate:
            item?.document
                ?.documentDate ||
            payment?.document
                ?.documentDate ||
            invoice?.documentDate ||
            null,
    };

    /*
     * Build a fallback allocation record. This is used when older
     * payments have no separate Payment_Allocation document.
     */

    const fallbackAllocation =
        allocatedAmount > 0
            ? {
                  _id:
                      allocationId ||
                      `${paymentId}-allocation`,

                  payment:
                      paymentId,

                  document,

                  documentNumber:
                      document.documentNumber,

                  allocationDate:
                      allocation
                          ?.allocationDate ||
                      item?.allocationDate ||
                      payment?.paymentDate ||
                      item?.paymentDate ||
                      item?.createdAt ||
                      null,

                  allocatedAmount,

                  activeAmount:
                      Math.max(
                          activeAmount -
                              reversedAmount,
                          0
                      ),

                  reversedAmount,

                  status:
                      isReversed
                          ? "REVERSED"
                          : allocationStatus,
              }
            : null;

    return {
        ...item,
        ...payment,

        _id:
            paymentId,

        id:
            paymentId,

        paymentId,

        allocationId,

        receiptNumber:
            payment?.receiptNumber ||
            item
                ?.paymentReceiptNumber ||
            item?.receiptNumber ||
            "-",

        paymentDate:
            payment?.paymentDate ||
            item?.paymentDate ||
            allocation?.allocationDate ||
            item?.allocationDate ||
            item?.createdAt ||
            null,

        paymentMethod:
            payment?.paymentMethod ||
            item?.paymentMethod ||
            "",

        referenceNumber:
            payment?.referenceNumber ||
            item?.referenceNumber ||
            "",

        amount:
            paymentAmount,

        paymentAmount,

        receivedAmount:
            paymentAmount,

        allocatedAmount,

        activeAmount:
            Math.max(
                activeAmount -
                    reversedAmount,
                0
            ),

        reversedAmount,

        unallocatedAmount,

        availableAmount:
            unallocatedAmount,

        status,

        paymentStatus:
            status,

        allocationStatus,

        isReversed,

        customer:
            paymentCustomer &&
            typeof paymentCustomer ===
                "object"
                ? paymentCustomer
                : paymentCustomerId,

        customerId:
            paymentCustomerId,

        websiteUser:
            paymentWebsiteUser &&
            typeof paymentWebsiteUser ===
                "object"
                ? paymentWebsiteUser
                : websiteUserId ||
                  null,

        websiteUserId,

        customerSnapshot,

        customerName:
            customerSnapshot
                .customerName,

        customerMobile:
            customerSnapshot.mobile,

        customerEmail:
            customerSnapshot.email,

        document,

        documentId:
            document._id,

        documentNumber:
            document.documentNumber,

        /*
         * This allows the details dialog to display the invoice
         * allocation even when the allocation API returns no rows.
         */

        allocations:
            fallbackAllocation
                ? [
                      fallbackAllocation,
                  ]
                : [],

        createdAt:
            payment?.createdAt ||
            item?.createdAt ||
            null,

        updatedAt:
            payment?.updatedAt ||
            item?.updatedAt ||
            null,
    };
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoicePaymentsPanel = ({
    invoice,
    onPaymentClick,
    onViewPayment,
    onInvoiceRefresh,
}) => {
    const dispatch =
        useDispatch();

    const paymentState =
        useSelector(
            saleInvoicePaymentsSelector
        );

    const loadingState =
        useSelector(
            saleInvoicePaymentLoadingSelector
        );

    const errorState =
        useSelector(
            saleInvoicePaymentErrorSelector
        );

    const summaryState =
        useSelector(
            saleInvoicePaymentSummarySelector
        );

    const invoiceId =
        getInvoiceId(invoice);

    const invoiceNumber =
        getInvoiceNumber(invoice);

    const loading =
        Boolean(
            typeof loadingState ===
                "boolean"
                ? loadingState
                : loadingState
                      ?.listLoading ||
                  loadingState
                      ?.fetchLoading ||
                  loadingState
                      ?.historyLoading ||
                  loadingState
                      ?.loading
        );

    const error =
        typeof errorState ===
        "string"
            ? errorState
            : errorState?.message ||
              errorState?.historyError ||
              errorState?.fetchError ||
              "";

    const payments =
        useMemo(() => {
            return getPaymentRows(
                paymentState
            ).map((item) =>
                normalizePayment(
                    item,
                    invoice
                )
            );
        }, [
            paymentState,
            invoice,
        ]);

    const calculatedSummary =
        useMemo(() => {
            return payments.reduce(
                (
                    result,
                    payment
                ) => {
                    result.paymentCount +=
                        1;

                    result.totalPaid =
                        roundMoney(
                            result.totalPaid +
                                toNumber(
                                    payment
                                        ?.activeAmount
                                )
                        );

                    result.totalReversed =
                        roundMoney(
                            result.totalReversed +
                                toNumber(
                                    payment
                                        ?.reversedAmount
                                )
                        );

                    return result;
                },
                {
                    paymentCount: 0,
                    totalPaid: 0,
                    totalReversed: 0,
                }
            );
        }, [payments]);

    const paymentCount =
        toNumber(
            summaryState
                ?.paymentCount ??
                summaryState
                    ?.entryCount ??
                summaryState?.count ??
                calculatedSummary
                    .paymentCount
        );

    const totalPaid =
        roundMoney(
            firstDefinedNumber(
                [
                    summaryState
                        ?.totalPaid,
                    summaryState
                        ?.activePaid,
                    summaryState
                        ?.paidAmount,
                    calculatedSummary
                        .totalPaid,
                ],
                0
            )
        );

    const totalReversed =
        roundMoney(
            firstDefinedNumber(
                [
                    summaryState
                        ?.totalReversed,
                    summaryState
                        ?.reversedAmount,
                    calculatedSummary
                        .totalReversed,
                ],
                0
            )
        );

    const loadPayments =
        useCallback(async () => {
            if (!invoiceId) {
                return;
            }

            try {
                await dispatch(
                    fetchSaleInvoicePaymentHistory({
                        invoiceId,
                        documentId:
                            invoiceId,
                        id:
                            invoiceId,
                        page: 1,
                        limit: 100,
                        includeReversals:
                            true,
                    })
                ).unwrap();
            } catch (requestError) {
                console.error(
                    "Unable to fetch sale invoice payments:",
                    requestError
                );
            }
        }, [
            dispatch,
            invoiceId,
        ]);

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    const handleViewPayment =
        (payment) => {
            const callback =
                onPaymentClick ||
                onViewPayment;

            if (
                typeof callback !==
                "function"
            ) {
                return;
            }

            callback(payment);
        };

    const handleRefresh =
        async () => {
            await loadPayments();

            if (
                typeof onInvoiceRefresh ===
                "function"
            ) {
                await onInvoiceRefresh();
            }
        };

    return (
        <section className="sale-invoice-payments-panel">
            <div className="sale-invoice-payments-panel-header">
                <div className="sale-invoice-payments-panel-heading">
                    <span>
                        <FiCreditCard />
                    </span>

                    <div>
                        <h3>
                            Payments
                        </h3>

                        <p>
                            Payments and allocations recorded against{" "}
                            <strong>
                                {invoiceNumber}
                            </strong>
                            .
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className="sale-invoice-payments-refresh"
                    disabled={loading}
                    onClick={
                        handleRefresh
                    }
                >
                    <FiRefreshCw
                        className={
                            loading
                                ? "sale-invoice-payments-spin"
                                : ""
                        }
                    />

                    Refresh
                </button>
            </div>

            <div className="sale-invoice-payments-summary">
                <div>
                    <span>
                        Payment Records
                    </span>

                    <strong>
                        {paymentCount}
                    </strong>
                </div>

                <div className="sale-invoice-payments-summary-paid">
                    <span>
                        Active Paid
                    </span>

                    <strong>
                        {formatCurrency(
                            totalPaid
                        )}
                    </strong>
                </div>

                <div className="sale-invoice-payments-summary-reversed">
                    <span>
                        Reversed
                    </span>

                    <strong>
                        {formatCurrency(
                            totalReversed
                        )}
                    </strong>
                </div>
            </div>

            {error && (
                <div className="sale-invoice-payments-error">
                    <FiAlertCircle />

                    <span>
                        {error}
                    </span>

                    <button
                        type="button"
                        onClick={
                            handleRefresh
                        }
                    >
                        Retry
                    </button>
                </div>
            )}

            <div className="sale-invoice-payments-table-wrap">
                <table className="sale-invoice-payments-table">
                    <thead>
                        <tr>
                            <th>
                                Receipt
                            </th>

                            <th>
                                Payment Date
                            </th>

                            <th>
                                Method
                            </th>

                            <th>
                                Reference
                            </th>

                            <th className="sale-invoice-payments-text-right">
                                Payment
                            </th>

                            <th className="sale-invoice-payments-text-right">
                                Allocated
                            </th>

                            <th className="sale-invoice-payments-text-right">
                                Available
                            </th>

                            <th>
                                Status
                            </th>

                            <th className="sale-invoice-payments-action-heading">
                                Action
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan="9"
                                    className="sale-invoice-payments-loading"
                                >
                                    <FiLoader className="sale-invoice-payments-spin" />

                                    Loading payments...
                                </td>
                            </tr>
                        ) : payments.length ===
                          0 ? (
                            <tr>
                                <td
                                    colSpan="9"
                                    className="sale-invoice-payments-empty"
                                >
                                    <FiFileText />

                                    <strong>
                                        No payments found
                                    </strong>

                                    <span>
                                        Payments recorded against this invoice will appear here.
                                    </span>
                                </td>
                            </tr>
                        ) : (
                            payments.map(
                                (
                                    payment,
                                    index
                                ) => {
                                    const status =
                                        String(
                                            payment
                                                ?.paymentStatus ||
                                                "ACTIVE"
                                        ).toUpperCase();

                                    const isReversed =
                                        payment
                                            ?.isReversed ===
                                            true ||
                                        status ===
                                            "REVERSED";

                                    const statusClass =
                                        isReversed
                                            ? "reversed"
                                            : status
                                                  .toLowerCase()
                                                  .replace(
                                                      /_/g,
                                                      "-"
                                                  );

                                    return (
                                        <tr
                                            key={
                                                payment
                                                    ?.paymentId ||
                                                payment
                                                    ?.allocationId ||
                                                index
                                            }
                                            className={
                                                isReversed
                                                    ? "sale-invoice-payments-row-reversed"
                                                    : ""
                                            }
                                        >
                                            <td>
                                                <button
                                                    type="button"
                                                    className="sale-invoice-payments-receipt-button"
                                                    onClick={() =>
                                                        handleViewPayment(
                                                            payment
                                                        )
                                                    }
                                                >
                                                    {payment
                                                        ?.receiptNumber ||
                                                        "-"}
                                                </button>

                                                <small>
                                                    {formatDate(
                                                        payment
                                                            ?.createdAt,
                                                        true
                                                    )}
                                                </small>
                                            </td>

                                            <td>
                                                {formatDate(
                                                    payment
                                                        ?.paymentDate
                                                )}
                                            </td>

                                            <td>
                                                {formatLabel(
                                                    payment
                                                        ?.paymentMethod
                                                )}
                                            </td>

                                            <td>
                                                {payment
                                                    ?.referenceNumber ||
                                                    "-"}
                                            </td>

                                            <td className="sale-invoice-payments-text-right sale-invoice-payments-amount">
                                                {formatCurrency(
                                                    payment
                                                        ?.amount
                                                )}
                                            </td>

                                            <td className="sale-invoice-payments-text-right">
                                                {formatCurrency(
                                                    payment
                                                        ?.allocatedAmount
                                                )}
                                            </td>

                                            <td className="sale-invoice-payments-text-right sale-invoice-payments-available">
                                                {formatCurrency(
                                                    payment
                                                        ?.unallocatedAmount
                                                )}
                                            </td>

                                            <td>
                                                <span
                                                    className={`sale-invoice-payments-status sale-invoice-payments-status-${statusClass}`}
                                                >
                                                    {formatLabel(
                                                        isReversed
                                                            ? "REVERSED"
                                                            : status
                                                    )}
                                                </span>
                                            </td>

                                            <td className="sale-invoice-payments-actions">
                                                <button
                                                    type="button"
                                                    className="sale-invoice-payment-view-button"
                                                    onClick={() =>
                                                        handleViewPayment(
                                                            payment
                                                        )
                                                    }
                                                >
                                                    <FiEye />

                                                    View Payment
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                }
                            )
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default
    SaleInvoicePaymentsPanel;